import React from "react";
import { usePaginationState } from "@components/shared/DataTable/Pagination";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";

import DataTableRenderer from "@components/shared/DataTable/DataTableRenderer";
import ApprovalTableActionButtons from "@components/shared/ApprovalTableActionButtons";
import ApprovalTag from "@components/shared/ApprovalTag";
import IndeterminateCheckbox from "@components/form/IndeterminateCheckbox";
import { useAllAdministrativeApprovals } from "@hooks/query/zenith/useAllAdministrativeApprovals";
import { AdministrativeApprovalNumericType } from "@services/administrativeApprovals";
import { getActionTimestamp } from "@utils/administrativeApproval";

const PermanentTransferTable = () => {
  const { t } = useTranslation();
  const [pagination, setPagination] = usePaginationState();

  const {
    data: { administrativeApprovals, meta } = {
      administrativeApprovals: [],
      meta: {},
    },
    isLoading,
    isFetching,
  } = useAllAdministrativeApprovals({
    params: {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      types: [AdministrativeApprovalNumericType.PT],
    },
  });

  const { refetch: fetchAll, isFetching: isFetchingAll } =
    useAllAdministrativeApprovals({
      params: {
        types: [AdministrativeApprovalNumericType.PT],
        limit: -1,
      },
    });

  const columns = React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <IndeterminateCheckbox
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ table, row }) => {
          const selectedIds = table
            .getSelectedRowModel()
            .rows.map(row => row.id);

          return (
            <IndeterminateCheckbox
              checked={selectedIds.includes(row.id)}
              disabled={!row.getCanSelect()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          );
        },
      },
      {
        accessorKey: "seller.name",
        header: t(
          "approval.seasonal_water_assignments.from_subscriber",
        ) as string,
      },
      {
        accessorKey: "buyer.name",
        header: t(
          "approval.seasonal_water_assignments.to_subscriber",
        ) as string,
      },
      {
        header: t("common.level0wrs") as string,
        cell: info => {
          const { extractionRights = [] } = info.row.original;
          return extractionRights.length
            ? extractionRights[0].level0Resource?.identifier
            : "-";
        },
      },
      {
        header: t("common.extraction_rights") as any,
        cell: info => {
          return info.row.original.extractionRights
            .map((i: any) => i.name)
            .join(", ");
        },
      },
      {
        accessorKey: "status",
        header: t("common.status") as string,
        cell: ({ row }) => {
          const { status, accountedAt } = row.original;
          return status ? (
            <ApprovalTag isAccounted={Boolean(accountedAt)} status={status} />
          ) : null;
        },
      },
      {
        header: t("common.actioned_at") as string,
        cell: ({ row }) => {
          return getActionTimestamp(row.original);
        },
      },
      {
        id: "actions",
        header: t("common.actions") as string,
        cell: ({ row }) => <ApprovalTableActionButtons row={row} />,
      },
    ],
    [t],
  );

  return (
    <DataTableRenderer
      name={t("approval.permanent_trades.title")}
      columns={columns}
      data={administrativeApprovals}
      pagination={pagination}
      onPaginationChange={setPagination}
      pageCount={meta.lastPage ?? -1}
      isLoading={isLoading}
      isFetching={isFetching || isFetchingAll}
      fetchAll={fetchAll}
      fetchAllDataKey="administrativeApprovals"
      stickyHeader
    />
  );
};

export default PermanentTransferTable;
